/**
 * Host-decision panel (winner only, at RESULT). The winner chooses to host the
 * next standup themselves or force the worst loser to host. Sends `hostChoice`.
 * Default on timeout = worst loser (server-enforced, GAME_SPEC §9). Once the
 * server reports `hostNextId`, we show the outcome instead of the buttons.
 */

import { HOUSE_ID } from '@blackjack/shared';
import { useGameStore } from '../state/store';
import { useSend } from '../net/RealtimeProvider';

export function HostChoice() {
  const send = useSend();
  const phase = useGameStore((s) => s.server.phase);
  const result = useGameStore((s) => s.server.result);
  const roster = useGameStore((s) => s.server.roster);
  const selfId = useGameStore((s) => s.serverAssignedId ?? s.identity?.clientId);
  const role = useGameStore((s) => s.identity?.role);

  if (phase !== 'RESULT' || !result) return null;

  const nameFor = (id: string | null) =>
    id == null ? '—' : id === HOUSE_ID ? 'House' : (roster.find((r) => r.id === id)?.name ?? id.slice(0, 6));

  // The croupier OWNS the house hand, so a house win is the croupier's win — they
  // make the host choice (GAME_SPEC §7/§9).
  const amWinner = result.winnerId === selfId || (result.winnerId === HOUSE_ID && role === 'croupier');
  const decided = result.hostNextId != null;

  if (decided) {
    return (
      <div className="panel hostchoice">
        <h3 className="panel__title heading-gold">Next Standup Host</h3>
        <p className="hostchoice__decided title-gold">{nameFor(result.hostNextId)}</p>
      </div>
    );
  }

  if (!amWinner) {
    return (
      <div className="panel hostchoice">
        <h3 className="panel__title heading-gold">Host Decision</h3>
        <p className="hostchoice__waiting">
          Waiting for {nameFor(result.winnerId)} to choose the next host…
        </p>
      </div>
    );
  }

  return (
    <div className="panel hostchoice">
      <h3 className="panel__title heading-gold">You Won! Who hosts tomorrow?</h3>
      <div className="hostchoice__buttons">
        <button type="button" className="btn-gold" onClick={() => send({ type: 'hostChoice', choice: 'self' })}>
          I'll host
        </button>
        <button
          type="button"
          className="btn-action btn-stand"
          onClick={() => send({ type: 'hostChoice', choice: 'worstLoser' })}
        >
          Make {nameFor(result.worstLoserId)} host
        </button>
      </div>
    </div>
  );
}
